import React from 'react';
import type { BanType } from '../../../types/ban';

interface AdminBanTableProps {
  type: BanType;
  data: any[];
  isLoading: boolean;
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  onEdit: (ban: any) => void;
  onDelete: (id: number) => void;
  onToggleStatus: (ban: any) => void;
}

export default function AdminBanTable({
  type,
  data,
  isLoading,
  selectedIds,
  onSelectionChange,
  onEdit,
  onDelete,
  onToggleStatus,
}: AdminBanTableProps) {
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      onSelectionChange(data.map((ban) => ban.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectOne = (id: number) => {
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  const getValueField = () => {
    switch (type) {
      case 'ip':
        return 'ip_address';
      case 'domain':
        return 'domain';
      case 'isp':
        return 'isp_name';
      case 'asn':
        return 'asn';
    }
  };

  const getValueLabel = () => {
    switch (type) {
      case 'ip':
        return 'IP Address';
      case 'domain':
        return 'Domain';
      case 'isp':
        return 'ISP Name';
      case 'asn':
        return 'ASN';
    }
  };

  if (isLoading) {
    return (
      <div className="card p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p className="mt-2 text-gray-600">Loading bans...</p>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="card p-8 text-center">
        <div className="mx-auto h-12 w-12 text-gray-400">
          <svg
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
            />
          </svg>
        </div>
        <p className="mt-2 text-gray-500">No bans found</p>
        <p className="text-sm text-gray-400">
          Add your first {type.toUpperCase()} ban to get started
        </p>
      </div>
    );
  }

  const valueField = getValueField();
  const valueLabel = getValueLabel();

  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="p-4 w-12">
                <input
                  type="checkbox"
                  checked={
                    data.length > 0 && selectedIds.length === data.length
                  }
                  onChange={handleSelectAll}
                  className="rounded border-gray-300"
                />
              </th>
              <th className="text-left p-4 font-medium text-gray-700">
                {valueLabel}
              </th>
              {type === 'asn' && (
                <th className="text-left p-4 font-medium text-gray-700">
                  ASN Name
                </th>
              )}
              <th className="text-left p-4 font-medium text-gray-700">
                Reason
              </th>
              <th className="text-left p-4 font-medium text-gray-700">
                Status
              </th>
              <th className="text-left p-4 font-medium text-gray-700">
                Created At
              </th>
              <th className="text-right p-4 font-medium text-gray-700">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((ban) => (
              <tr
                key={ban.id}
                className="border-b hover:bg-gray-50 transition"
              >
                <td className="p-4">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(ban.id)}
                    onChange={() => handleSelectOne(ban.id)}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="p-4">
                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                    {ban[valueField]}
                  </code>
                </td>
                {type === 'asn' && (
                  <td className="p-4 text-gray-700">
                    {ban.asn_name || (
                      <span className="text-gray-400 italic">N/A</span>
                    )}
                  </td>
                )}
                <td className="p-4 text-gray-700">
                  {ban.reason || (
                    <span className="text-gray-400 italic">
                      No reason provided
                    </span>
                  )}
                </td>
                <td className="p-4">
                  <button
                    onClick={() => onToggleStatus(ban)}
                    className={`px-2 py-1 rounded text-xs font-medium transition ${
                      ban.is_active
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                    }`}
                  >
                    {ban.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td className="p-4 text-gray-600 text-sm">
                  {new Date(ban.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </td>
                <td className="p-4 text-right space-x-2">
                  <button
                    onClick={() => onEdit(ban)}
                    className="btn btn-secondary btn-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(ban.id)}
                    className="btn btn-danger btn-sm"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
